import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5176/api';

const api = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
    failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve());
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;

        if (error.response?.status !== 401 || original._retry) return Promise.reject(error);
        if (original.url?.includes('/auth/refresh') || original.url?.includes('/auth/logout')) return Promise.reject(error);

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(() => api(original));
        }

        original._retry = true;
        isRefreshing = true;

        try {
            const { data } = await api.post('/auth/refresh', {}, { withCredentials: true });
            localStorage.setItem('token', data.token);
            original.headers.Authorization = `Bearer ${data.token}`;
            processQueue(null);
            window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', { detail: { token: data.token } }));
            return api(original);
        } catch (refreshError) {
            const latestToken = localStorage.getItem('token');
            const originalToken = error.config.headers?.Authorization?.replace('Bearer ', '');
            if (latestToken && latestToken !== originalToken) {
                original.headers.Authorization = `Bearer ${latestToken}`;
                processQueue(null);
                isRefreshing = false;
                return api(original);
            }
            processQueue(refreshError);
            localStorage.removeItem('token');
            window.dispatchEvent(new Event('auth:logout'));
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export const chatService = {
    getChats: () => api.get('/chats'),
    getChat: (id) => api.get(`/chats/${id}`),
    createOrGetChat: (targetUserId) => api.post('/chats', { targetUserId }),
    getMessages: (chatId, page = 1) => api.get(`/chats/${chatId}/messages`, { params: { page } }),
    sendMessage: (chatId, content) => api.post(`/chats/${chatId}/messages`, { content }),
    uploadAttachment: (chatId, messageId, file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/chats/${chatId}/messages/${messageId}/attachments`, form, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    deleteMessage: (chatId, messageId) => api.delete(`/chats/${chatId}/messages/${messageId}`),
    markAsRead: (chatId) => api.post(`/chats/${chatId}/messages/read`),
    getUnreadCount: () => api.get('/chats/unread-count'),
};

export const adService = {
    getMyAds: () => api.get('/ads/mine'),
    deleteAd: (id) => api.delete(`/ads/${id}`),
    archiveAd: (id) => api.post(`/ads/${id}/archive`),
    unarchiveAd: (id) => api.post(`/ads/${id}/unarchive`),
    incrementView: (id) => api.post(`/ads/${id}/view`),
    incrementContactView: (id) => api.post(`/ads/${id}/contact-view`),
    getViewsStats: (id, days = 30) => api.get(`/ads/${id}/views-stats`, { params: { days } }),
    getPriceHistory: (id) => api.get(`/ads/${id}/price-history`),
};

export const favoriteService = {
    getFavorites: () => api.get('/favorites'),
    addFavorite: (adId) => api.post(`/favorites/${adId}`),
    removeFavorite: (adId) => api.delete(`/favorites/${adId}`),
};

export const hiddenService = {
    getHiddenAds: () => api.get('/hidden/ads'),
    hideAd: (adId) => api.post(`/hidden/ads/${adId}`),
    unhideAd: (adId) => api.delete(`/hidden/ads/${adId}`),
    getHiddenSellers: () => api.get('/hidden/sellers'),
    hideSeller: (userId) => api.post(`/hidden/sellers/${userId}`),
    unhideSeller: (userId) => api.delete(`/hidden/sellers/${userId}`),
};

export const authService = {
    register: (name, email, password) =>
        api.post('/auth/register', { name, email, password }),

    login: (email, password) =>
        api.post('/auth/login', { email, password }),

    refresh: () =>
        api.post('/auth/refresh', {}, { withCredentials: true }),

    logout: () =>
        api.post('/auth/logout', {}, { withCredentials: true }),
};

export const reportService = {
    createReport: (adId, reason, comment) =>
        api.post('/reports', { adId, reason, comment }),
};

export const notificationService = {
    getNotifications: () => api.get('/notifications'),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    markAsRead: (id) => api.post(`/notifications/${id}/read`),
    markAllAsRead: () => api.post('/notifications/read-all'),
};

export const priceTrackingService = {
    getTracked: () => api.get('/price-tracking'),
    track: (adId) => api.post(`/price-tracking/${adId}`),
    untrack: (adId) => api.delete(`/price-tracking/${adId}`),
};

export const commentService = {
    getComments: (adId, page = 1, sortBy = 'new') =>
        api.get(`/ads/${adId}/comments`, { params: { page, sortBy } }),
    createComment: (adId, content, parentCommentId = null) =>
        api.post(`/ads/${adId}/comments`, { content, parentCommentId }),
    rateComment: (adId, commentId, type) =>
        api.post(`/ads/${adId}/comments/${commentId}/rating`, { type }),
    deleteComment: (adId, commentId) =>
        api.delete(`/ads/${adId}/comments/${commentId}`),
};

export const userService = {
    getProfile: (userId) => api.get(`/users/${userId}`),
    getReviews: (userId, page = 1) => api.get(`/users/${userId}/reviews`, { params: { page } }),
    submitReview: (userId, rating, content) => api.post(`/users/${userId}/reviews`, { rating, content }),
    deleteMyReview: (userId) => api.delete(`/users/${userId}/reviews/mine`),
    getUserAds: (userId) => api.get('/ads', { params: { userId } }),
};

export const newsService = {
    getList: (page = 1, pageSize = 12) => api.get('/news', { params: { page, pageSize } }),
    getBySlug: (slug) => api.get(`/news/${slug}`),
};

export const articlesService = {
    getList: (page = 1, pageSize = 12) => api.get('/articles', { params: { page, pageSize } }),
    getBySlug: (slug) => api.get(`/articles/${slug}`),
};

export const usefulLinksService = {
    getList: () => api.get('/useful-links'),
};

export const adminService = {
    getReports: (status) => api.get('/admin/reports', { params: status ? { status } : {} }),
    resolveReport: (id, action) => api.post(`/admin/reports/${id}/resolve`, { action }),

    getAds: (params) => api.get('/admin/ads', { params }),
    setAdStatus: (id, status) => api.put(`/admin/ads/${id}/status`, { status }),
    bulkSetAdStatus: (ids, status) => api.put('/admin/ads/status', { ids, status }),
    deleteAd: (id) => api.delete(`/admin/ads/${id}`),

    importAd: (url, sessionCookie, fingerprint) => api.post('/admin/import', { url, sessionCookie, fingerprint }),

    getComments: (params) => api.get('/admin/comments', { params }),
    deleteComment: (id) => api.delete(`/admin/comments/${id}`),

    getUsers: (params) => api.get('/admin/users', { params }),
    getUser: (id) => api.get(`/admin/users/${id}`),
    updateUser: (id, payload) => api.put(`/admin/users/${id}`, payload),
    banUser: (id) => api.put(`/admin/users/${id}/ban`),
    unbanUser: (id) => api.put(`/admin/users/${id}/unban`),

    getContentPosts: (type, params) => api.get('/admin/content-posts', { params: { type, ...params } }),
    getContentPost: (id) => api.get(`/admin/content-posts/${id}`),
    createContentPost: (type, payload) => api.post('/admin/content-posts', payload, { params: { type } }),
    updateContentPost: (id, payload) => api.put(`/admin/content-posts/${id}`, payload),
    deleteContentPost: (id) => api.delete(`/admin/content-posts/${id}`),
    uploadContentPostCover: (file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post('/admin/content-posts/cover', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    uploadContentPostImage: (file) => {
        const form = new FormData();
        form.append('image', file);
        return api.post('/admin/content-posts/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },

    getUsefulLinks: () => api.get('/admin/useful-links'),
    createUsefulLink: (payload) => api.post('/admin/useful-links', payload),
    updateUsefulLink: (id, payload) => api.put(`/admin/useful-links/${id}`, payload),
    deleteUsefulLink: (id) => api.delete(`/admin/useful-links/${id}`),
    uploadUsefulLinkLogo: (file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post('/admin/useful-links/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
};

export default api;
